/**
 * Utility functions for handling local storage operations
 */

import { StoreDetails, DeliveryCharge, Discount } from '../types';

// Define PrinterSettings interface locally
interface PrinterSettings {
  defaultPrintMethod: 'thermal' | 'browser';
  autoConnect: boolean;
  debugMode: boolean;
}

// Local storage keys
const STORAGE_KEYS = {
  STORE_DETAILS: 'cps_store_details',
  DELIVERY_CHARGE: 'cps_delivery_charge',
  DISCOUNT: 'cps_discount',
  PRINTER_SETTINGS: 'cps_printer_settings',
  TELEGRAM_CONFIG: 'cps_telegram_config',
};

/**
 * Save store details to local storage
 */
export const saveStoreDetails = (storeDetails: StoreDetails): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.STORE_DETAILS, JSON.stringify(storeDetails));
  } catch (error) {
    console.error('Error saving store details to local storage:', error);
  }
};

/**
 * Load store details from local storage
 * @returns StoreDetails object or null if not found
 */
export const loadStoreDetails = (): StoreDetails | null => {
  try {
    const storedData = localStorage.getItem(STORAGE_KEYS.STORE_DETAILS);
    if (storedData) {
      return JSON.parse(storedData) as StoreDetails;
    }
    return null;
  } catch (error) {
    console.error('Error loading store details from local storage:', error);
    return null;
  }
};

/**
 * Clear store details from local storage
 */
export const clearStoreDetails = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEYS.STORE_DETAILS);
  } catch (error) {
    console.error('Error clearing store details from local storage:', error);
  }
};

/**
 * Save delivery charge settings to local storage
 */
export const saveDeliveryCharge = (deliveryCharge: DeliveryCharge): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.DELIVERY_CHARGE, JSON.stringify(deliveryCharge));
  } catch (error) {
    console.error('Error saving delivery charge to local storage:', error);
  }
};

/**
 * Load delivery charge settings from local storage
 * @returns DeliveryCharge object or null if not found
 */
export const loadDeliveryCharge = (): DeliveryCharge | null => {
  try {
    const storedData = localStorage.getItem(STORAGE_KEYS.DELIVERY_CHARGE);
    if (storedData) {
      return JSON.parse(storedData) as DeliveryCharge;
    }
    return null;
  } catch (error) {
    console.error('Error loading delivery charge from local storage:', error);
    return null;
  }
};

/**
 * Clear delivery charge settings from local storage
 */
export const clearDeliveryCharge = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEYS.DELIVERY_CHARGE);
  } catch (error) {
    console.error('Error clearing delivery charge from local storage:', error);
  }
};

/**
 * Save discount settings to local storage
 */
export const saveDiscount = (discount: Discount): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.DISCOUNT, JSON.stringify(discount));
  } catch (error) {
    console.error('Error saving discount to local storage:', error);
  }
};

/**
 * Load discount settings from local storage
 * @returns Discount object or null if not found
 */
export const loadDiscount = (): Discount | null => {
  try {
    const storedData = localStorage.getItem(STORAGE_KEYS.DISCOUNT);
    if (storedData) {
      return JSON.parse(storedData) as Discount;
    }
    return null;
  } catch (error) {
    console.error('Error loading discount from local storage:', error);
    return null;
  }
};

/**
 * Clear discount settings from local storage
 */
export const clearDiscount = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEYS.DISCOUNT);
  } catch (error) {
    console.error('Error clearing discount from local storage:', error);
  }
};

/**
 * Save printer settings to local storage
 */
export const savePrinterSettings = (settings: PrinterSettings): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.PRINTER_SETTINGS, JSON.stringify(settings));
  } catch (error) {
    console.error('Error saving printer settings to local storage:', error);
  }
};

/**
 * Load printer settings from local storage
 * @returns PrinterSettings object or null if not found
 */
export const loadPrinterSettings = (): PrinterSettings | null => {
  try {
    const storedData = localStorage.getItem(STORAGE_KEYS.PRINTER_SETTINGS);
    if (storedData) {
      return JSON.parse(storedData) as PrinterSettings;
    }
    return null;
  } catch (error) {
    console.error('Error loading printer settings from local storage:', error);
    return null;
  }
};

/**
 * Clear printer settings from local storage
 */
export const clearPrinterSettings = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEYS.PRINTER_SETTINGS);
  } catch (error) {
    console.error('Error clearing printer settings from local storage:', error);
  }
};
